import React, {
  type ReactNode,
  type RefObject,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { useTheme, alpha, type SxProps, type Theme } from '@mui/material/styles';
import { Scrollbars, type positionValues } from 'react-custom-scrollbars-2';
import { useResizeDetector, type ResizePayload } from 'react-resize-detector';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import mergeSxProps from '../shared/mergeSxProps';

/** Imperative API for `scrollBoxRef`. */
export type ScrollBoxHandle = {
  scrollbars: Scrollbars;
  /** Clears the peak min-height so content can shrink to its natural height. */
  resetStableScrollHeight: () => void;

  /**
   * Clears the peak min-height and ignores new peaks until `resumeStableScrollHeight`.
   * Call this before an animated shrink so the scroller follows the animation.
   */
  suspendStableScrollHeight: () => void;
  /** Ends a suspend and records the current content height as the new peak. */
  resumeStableScrollHeight: () => void;
  engageAutoScroll: () => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
};

export type ScrollBoxRef = RefObject<ScrollBoxHandle | null>;

/**
 * Next peak min-height for a stable scroller.
 * Suspended updates and non-positive candidates leave the previous peak unchanged.
 * @param previousPeak The peak recorded so far, in pixels.
 * @param candidate The latest content height, in pixels.
 * @param suspended When true, the peak does not move.
 * @returns The peak to keep, in pixels.
 */
export function nextStableContentPeak(
  previousPeak: number,
  candidate: number,
  suspended: boolean,
): number {
  if (suspended || candidate <= 0) return previousPeak;
  return Math.max(previousPeak, Math.round(candidate));
}

const AUTO_SCROLL_THRESHOLD_DEFAULT = 10;
const FADE_SIZE_DEFAULT = 60;
/** Max time to treat smooth scroll as programmatic (avoids disengaging auto-scroll mid-animation). */
const SMOOTH_PROGRAMMATIC_SCROLL_MAX_MS = 2000;
const SMOOTH_PROGRAMMATIC_SCROLL_POLL_MS = 50;

const supportsScrollTimeline =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('animation-timeline', 'scroll()');

type ScrollFadeMaskOptions = {
  fadeSize: number;
  disableTopFade: boolean;
  overflowXHidden: boolean;
  overflowYHidden: boolean;
};

/** Builds edge-fade mask layers for browsers without scroll-driven animations (Firefox). */
function buildScrollFadeMask(
  values: Pick<
    positionValues,
    'scrollTop' | 'scrollLeft' | 'scrollHeight' | 'scrollWidth' | 'clientHeight' | 'clientWidth'
  >,
  options: ScrollFadeMaskOptions,
): { maskImage: string; maskComposite: string } | null {
  const { fadeSize, disableTopFade, overflowXHidden, overflowYHidden } = options;
  const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = values;
  const masks: string[] = [];

  if (!overflowYHidden) {
    const fadeTopPos = Math.round(Math.min(scrollTop, fadeSize));
    const maxScrollTop = scrollHeight - clientHeight;
    const fadeBottomPos = Math.round(Math.min(maxScrollTop - scrollTop, fadeSize));

    if (disableTopFade) {
      masks.push(`linear-gradient(rgb(0 0 0 / 100%) calc(100% - ${fadeBottomPos}px), transparent)`);
    } else {
      masks.push(
        `linear-gradient(transparent, rgb(0 0 0 / 80%) ${fadeTopPos}px, rgb(0 0 0 / 100%) calc(100% - ${fadeBottomPos}px), transparent)`,
      );
    }
  }

  if (!overflowXHidden) {
    const fadeLeftPos = Math.round(Math.min(scrollLeft, fadeSize));
    const maxScrollLeft = scrollWidth - clientWidth;
    const fadeRightPos = Math.round(Math.min(maxScrollLeft - scrollLeft, fadeSize));
    masks.push(
      `linear-gradient(to right, transparent, rgb(0 0 0 / 80%) ${fadeLeftPos}px, rgb(0 0 0 / 100%) calc(100% - ${fadeRightPos}px), transparent)`,
    );
  }

  if (masks.length === 0) return null;

  return {
    maskImage: masks.join(', '),
    maskComposite: masks.length > 1 ? 'intersect' : '',
  };
}

export type ScrollBoxProps = {
  children: ReactNode;
  className?: string;
  startAtBottom?: boolean;
  paddingBottom?: number;
  scrollBoxRef?: ScrollBoxRef;
  fadeSize?: number;
  scrollBehavior?: 'auto' | 'smooth';
  scrollTrackOutside?: boolean;
  disableTopFade?: boolean;
  disableFade?: boolean;
  overflowXHidden?: boolean;
  overflowYHidden?: boolean;
  innerSx?: SxProps<Theme>;
  onScroll?: (size: number, position: number, values: positionValues) => void;
  /** Pin to bottom while streaming; user scroll-up disengages. */
  autoScroll?: boolean;
  onAutoScrollActiveChange?: (active: boolean) => void;
  autoScrollThreshold?: number;
  initialAutoScrollActive?: boolean;
  /** 0 = top, 1 = bottom; used when initialAutoScrollActive is false. */
  initialScrollRatio?: number;
  /** Keep minHeight at content peak to avoid shrink jumps. */
  stableScrollHeight?: boolean;
};

export default function ScrollBox({
  children,
  className,
  startAtBottom,
  paddingBottom,
  scrollBoxRef,
  fadeSize = 40,
  scrollBehavior = 'auto',
  scrollTrackOutside = false,
  onScroll,
  disableTopFade = false,
  disableFade = false,
  overflowXHidden = false,
  overflowYHidden = false,
  autoScroll = false,
  onAutoScrollActiveChange,
  autoScrollThreshold = AUTO_SCROLL_THRESHOLD_DEFAULT,
  initialAutoScrollActive = true,
  initialScrollRatio,
  stableScrollHeight = false,
  innerSx,
}: ScrollBoxProps) {
  'use memo';

  const theme = useTheme();
  const backgroundColor = alpha(theme.palette.customColors.main, 0.2);

  const scrollerRef = useRef<Scrollbars | null>(null);
  const scrollViewRef = useRef<HTMLDivElement | null>(null);
  const innerContentElRef = useRef<HTMLDivElement | null>(null);
  // Sidebars omit autoScroll; do not inherit chat's default pinned-to-bottom state.
  const isAutoScrollActiveRef = useRef(autoScroll && initialAutoScrollActive);
  /** True while scroll position is being set by ScrollBox (not the user). */
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollClearTimerRef = useRef<number | null>(null);
  const maxStableContentHeightRef = useRef(0);
  const stableScrollHeightSuspendedRef = useRef(false);
  const savedScrollRatioRef = useRef<number | null>(
    initialAutoScrollActive ? null : (initialScrollRatio ?? null),
  );
  const prevClientHeightRef = useRef(0);
  const lastContentHeightRef = useRef(0);
  const [stableContentMinHeight, setStableContentMinHeight] = useState(0);

  useEffect(() => {
    if (!stableScrollHeight) {
      stableScrollHeightSuspendedRef.current = false;
      maxStableContentHeightRef.current = 0;
      setStableContentMinHeight(0);
    }
  }, [stableScrollHeight]);

  // keep: effect dependency stability
  const resetStableScrollHeight = useCallback((): void => {
    maxStableContentHeightRef.current = 0;
    setStableContentMinHeight(0);
  }, []);

  // keep: effect dependency stability
  const suspendStableScrollHeight = useCallback((): void => {
    stableScrollHeightSuspendedRef.current = true;
    maxStableContentHeightRef.current = 0;
    setStableContentMinHeight(0);
  }, []);

  // keep: effect dependency stability
  const resumeStableScrollHeight = useCallback((): void => {
    stableScrollHeightSuspendedRef.current = false;
    const contentHeight = Math.round(innerContentElRef.current?.offsetHeight ?? 0);
    maxStableContentHeightRef.current = contentHeight > 0 ? contentHeight : 0;
    queueMicrotask(() => {
      if (stableScrollHeightSuspendedRef.current) return;
      setStableContentMinHeight(maxStableContentHeightRef.current);
    });
  }, []);

  // keep: effect dependency stability
  const recordStableContentPeak = useCallback(
    (candidate: number) => {
      const prev = maxStableContentHeightRef.current;
      const next = nextStableContentPeak(
        prev,
        candidate,
        !stableScrollHeight || stableScrollHeightSuspendedRef.current,
      );
      if (next > prev) {
        maxStableContentHeightRef.current = next;
        // Defer — onResize can run during render.
        queueMicrotask(() => {
          if (stableScrollHeightSuspendedRef.current) return;
          if (maxStableContentHeightRef.current !== next) return;
          setStableContentMinHeight(next);
        });
      }
    },
    [stableScrollHeight],
  );

  // keep: effect dependency stability
  const setAutoScrollActive = useCallback(
    (active: boolean): void => {
      if (!autoScroll || isAutoScrollActiveRef.current === active) return;
      isAutoScrollActiveRef.current = active;
      if (!onAutoScrollActiveChange) return;
      // Defer — onResize can run during render.
      queueMicrotask(() => {
        onAutoScrollActiveChange(active);
      });
    },
    [autoScroll, onAutoScrollActiveChange],
  );

  // keep: effect dependency stability
  const engageAutoScroll = useCallback((): void => {
    setAutoScrollActive(true);
  }, [setAutoScrollActive]);

  // keep: effect dependency stability
  const beginProgrammaticScroll = useCallback(
    (behavior: ScrollBehavior): void => {
      isProgrammaticScrollRef.current = true;
      if (programmaticScrollClearTimerRef.current != null) {
        window.clearTimeout(programmaticScrollClearTimerRef.current);
        programmaticScrollClearTimerRef.current = null;
      }
      const clearProgrammaticScroll = (): void => {
        isProgrammaticScrollRef.current = false;
        programmaticScrollClearTimerRef.current = null;
      };
      if (behavior !== 'smooth') {
        requestAnimationFrame(() => {
          requestAnimationFrame(clearProgrammaticScroll);
        });
        return;
      }

      const startedAt = Date.now();
      const pollUntilSmoothSettled = (): void => {
        const scroller = scrollerRef.current;
        if (!scroller) {
          clearProgrammaticScroll();
          return;
        }
        const { scrollTop, scrollHeight, clientHeight } = scroller.getValues();
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const atBottom = distanceFromBottom < autoScrollThreshold;
        const timedOut = Date.now() - startedAt >= SMOOTH_PROGRAMMATIC_SCROLL_MAX_MS;
        if (atBottom || timedOut) {
          clearProgrammaticScroll();
          return;
        }
        programmaticScrollClearTimerRef.current = window.setTimeout(
          pollUntilSmoothSettled,
          SMOOTH_PROGRAMMATIC_SCROLL_POLL_MS,
        );
      };
      programmaticScrollClearTimerRef.current = window.setTimeout(
        pollUntilSmoothSettled,
        SMOOTH_PROGRAMMATIC_SCROLL_POLL_MS,
      );
    },
    [autoScrollThreshold],
  );

  // keep: effect dependency stability
  const syncAutoScrollFromScrollPosition = useCallback(
    (values: Pick<positionValues, 'scrollTop' | 'scrollHeight' | 'clientHeight'>): void => {
      if (!autoScroll || isProgrammaticScrollRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = values;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < autoScrollThreshold) {
        if (!isAutoScrollActiveRef.current) {
          engageAutoScroll();
        }
        return;
      }
      if (isAutoScrollActiveRef.current) {
        setAutoScrollActive(false);
      }
    },
    [autoScroll, autoScrollThreshold, engageAutoScroll, setAutoScrollActive],
  );

  // keep: effect dependency stability
  const scrollToTop = useCallback(
    (top: number, behavior: ScrollBehavior = 'auto') => {
      if (!scrollerRef.current) return;

      beginProgrammaticScroll(behavior);

      const scrollView = scrollerRef.current.container.querySelector(
        '.scroll-box-view',
      ) as HTMLElement | null;

      if (scrollView) {
        if (behavior === 'smooth') {
          scrollView.style.scrollBehavior = 'smooth';
        }
        scrollView.scrollTo({ top, behavior });
        if (behavior === 'smooth') {
          window.setTimeout(() => {
            scrollView.style.scrollBehavior = scrollBehavior;
          }, 400);
        }
        return;
      }

      scrollerRef.current.scrollTop(top);
    },
    [scrollBehavior, beginProgrammaticScroll],
  );

  // keep: effect dependency stability
  const restoreSavedScrollRatio = useCallback(
    (behavior: ScrollBehavior = 'auto'): void => {
      if (!scrollerRef.current || isAutoScrollActiveRef.current) return;
      const ratio = savedScrollRatioRef.current;
      if (ratio == null) return;

      const { scrollHeight, clientHeight } = scrollerRef.current.getValues();
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop <= 0) return;

      const targetTop = Math.min(Math.max(ratio * maxScrollTop, 0), maxScrollTop);
      scrollToTop(targetTop, behavior);
    },
    [scrollToTop],
  );

  // keep: effect dependency stability
  const handleScrollFrame = useCallback(
    (values: positionValues) => {
      const { scrollTop, scrollHeight, clientHeight } = values;

      // Firefox: update fade mask via DOM
      if (!supportsScrollTimeline && !disableFade && scrollViewRef.current) {
        const fadeMask = buildScrollFadeMask(values, {
          fadeSize,
          disableTopFade,
          overflowXHidden,
          overflowYHidden,
        });
        const el = scrollViewRef.current;
        if (fadeMask) {
          el.style.maskSize = '100% 100%';
          el.style.maskImage = fadeMask.maskImage;
          el.style.maskComposite = fadeMask.maskComposite;
        } else {
          el.style.maskImage = '';
          el.style.maskComposite = '';
        }
      }

      const maxScrollTop = scrollHeight - clientHeight;
      if (!isAutoScrollActiveRef.current && maxScrollTop > 0) {
        savedScrollRatioRef.current = scrollTop / maxScrollTop;
      }

      // Viewport resize: restore ratio or stay pinned to bottom.
      if (prevClientHeightRef.current > 0 && prevClientHeightRef.current !== clientHeight) {
        if (isAutoScrollActiveRef.current) {
          if (maxScrollTop > 0) {
            scrollToTop(maxScrollTop, 'auto');
          }
        } else {
          restoreSavedScrollRatio('auto');
        }
      }
      prevClientHeightRef.current = clientHeight;

      syncAutoScrollFromScrollPosition(values);

      if (onScroll) {
        const size = scrollHeight / clientHeight;
        let position = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
        position = isNaN(position) ? 0 : position;
        position = Math.min(position, 1);
        position = position >= 0.99 ? 1 : position;
        onScroll(size, position, values);
      }
    },
    [
      onScroll,
      disableFade,
      disableTopFade,
      fadeSize,
      overflowXHidden,
      overflowYHidden,
      restoreSavedScrollRatio,
      scrollToTop,
      syncAutoScrollFromScrollPosition,
    ],
  );

  // keep: effect dependency stability
  const snapToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto'): void => {
      if (!scrollerRef.current) return;
      const { scrollHeight, clientHeight } = scrollerRef.current.getValues();
      const bottomScrollTop = scrollHeight - clientHeight;
      if (bottomScrollTop <= 0) return;
      scrollToTop(bottomScrollTop, behavior);
    },
    [scrollToTop],
  );

  // keep: effect dependency stability
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = scrollBehavior): void => {
      engageAutoScroll();
      snapToBottom(behavior);

      // Content can grow during smooth scroll or while stable min-height catches up.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          snapToBottom('auto');
        });
      });
      if (behavior === 'smooth') {
        window.setTimeout(() => {
          snapToBottom('auto');
        }, 500);
      }
    },
    [engageAutoScroll, scrollBehavior, snapToBottom],
  );

  // keep: effect dependency stability
  const assignScrollerRef = useCallback(
    (instance: Scrollbars | null) => {
      scrollerRef.current = instance;
      if (!scrollBoxRef) return;
      scrollBoxRef.current = instance
        ? {
            scrollbars: instance,
            resetStableScrollHeight,
            suspendStableScrollHeight,
            resumeStableScrollHeight,
            engageAutoScroll,
            scrollToBottom,
          }
        : null;
    },
    [
      scrollBoxRef,
      resetStableScrollHeight,
      suspendStableScrollHeight,
      resumeStableScrollHeight,
      engageAutoScroll,
      scrollToBottom,
    ],
  );

  // Firefox: paint fade mask before first scroll.
  useEffect(() => {
    if (supportsScrollTimeline || disableFade || !scrollerRef.current) return;
    const frame = requestAnimationFrame(() => {
      const values = scrollerRef.current?.getValues();
      if (values) handleScrollFrame(values);
    });
    return () => cancelAnimationFrame(frame);
  }, [supportsScrollTimeline, disableFade, handleScrollFrame]);

  // keep: effect dependency stability
  const applyInitialScrollPosition = useCallback((): void => {
    if (!autoScroll || !scrollerRef.current) return;

    isAutoScrollActiveRef.current = initialAutoScrollActive;
    setAutoScrollActive(initialAutoScrollActive);

    if (initialAutoScrollActive) {
      savedScrollRatioRef.current = null;
      scrollToBottom('auto');
      return;
    }

    if (initialScrollRatio == null) return;

    savedScrollRatioRef.current = initialScrollRatio;
    restoreSavedScrollRatio('auto');
  }, [
    autoScroll,
    initialAutoScrollActive,
    initialScrollRatio,
    scrollToBottom,
    restoreSavedScrollRatio,
    setAutoScrollActive,
  ]);

  // Initial scroll: sync + rAF for async content growth.
  useLayoutEffect(() => {
    if (!autoScroll) return;
    applyInitialScrollPosition();
    const frame = requestAnimationFrame(() => {
      applyInitialScrollPosition();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoScroll, applyInitialScrollPosition]);

  useEffect(() => {
    return () => {
      if (programmaticScrollClearTimerRef.current != null) {
        window.clearTimeout(programmaticScrollClearTimerRef.current);
      }
    };
  }, []);

  // keep: effect dependency stability
  const performAutoScroll = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (!autoScroll || !isAutoScrollActiveRef.current || !scrollerRef.current) return;

      const { scrollHeight, clientHeight } = scrollerRef.current.getValues();
      const bottomScrollTop = scrollHeight - clientHeight;
      if (bottomScrollTop <= 0) return;

      scrollToTop(bottomScrollTop, behavior);
    },
    [autoScroll, scrollToTop],
  );

  // Re-pin to bottom on content growth when auto-scroll is active.
  useEffect(() => {
    if (!autoScroll) return;
    performAutoScroll();
  }, [children, autoScroll, performAutoScroll]);

  // keep: effect dependency stability
  const handleInnerResize = useCallback(
    (payload: ResizePayload) => {
      const contentHeight = typeof payload.height === 'number' ? payload.height : 0;
      if (contentHeight > 0) {
        lastContentHeightRef.current = contentHeight;
      }

      if (stableScrollHeight && contentHeight > 0) {
        recordStableContentPeak(contentHeight);
      }

      if (!scrollerRef?.current) return;
      const values = scrollerRef.current?.getValues();
      handleScrollFrame(values);

      if (isAutoScrollActiveRef.current) {
        performAutoScroll('auto');
      } else {
        restoreSavedScrollRatio('auto');
      }
    },
    [
      stableScrollHeight,
      recordStableContentPeak,
      handleScrollFrame,
      performAutoScroll,
      restoreSavedScrollRatio,
    ],
  );

  const { ref: resizeRef } = useResizeDetector({
    handleHeight: true,
    ...(stableScrollHeight ? {} : { refreshMode: 'debounce' as const, refreshRate: 200 }),
    ...(stableScrollHeight ? { observerOptions: { box: 'border-box' as const } } : {}),
    onResize: handleInnerResize,
  });

  // keep: resizeRef identity from useResizeDetector
  const setInnerContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerContentElRef.current = node;
      resizeRef(node);
    },
    [resizeRef],
  );

  useLayoutEffect(() => {
    if (!stableScrollHeight) return;
    const contentEl = innerContentElRef.current;
    const viewEl = scrollViewRef.current;
    recordStableContentPeak(Math.max(contentEl?.offsetHeight ?? 0, viewEl?.scrollHeight ?? 0));
  }, [children, stableScrollHeight, recordStableContentPeak]);

  useLayoutEffect(() => {
    if (!stableScrollHeight || !autoScroll || stableContentMinHeight <= 0) return;
    performAutoScroll();
  }, [stableContentMinHeight, stableScrollHeight, autoScroll, performAutoScroll]);

  const viewFadeClass = clsx(
    !disableFade &&
      supportsScrollTimeline &&
      !overflowYHidden &&
      (disableTopFade ? 'scroll-box-fade-no-top' : 'scroll-box-fade'),
    !disableFade && supportsScrollTimeline && !overflowXHidden && 'scroll-box-fade-x',
  );
  const viewFadeStyle: React.CSSProperties | undefined =
    viewFadeClass && fadeSize !== FADE_SIZE_DEFAULT
      ? { ['--scroll-fade-size' as string]: `${fadeSize}px` }
      : undefined;

  return (
    <Scrollbars
      className={clsx('scroll-box', className)}
      autoHide
      ref={assignScrollerRef}
      style={{
        width: scrollTrackOutside ? 'calc(100% + 30px)' : '100%',
        height: scrollTrackOutside ? 'calc(100% + 30px)' : '100%',
        position: 'relative',
        margin: scrollTrackOutside ? '-15px' : undefined,
      }}
      onScrollFrame={handleScrollFrame}
      renderView={({ style, ...rest }) => (
        <div
          ref={scrollViewRef}
          className={clsx('scroll-box-view', viewFadeClass)}
          style={{
            ...style,
            ...viewFadeStyle,
            scrollBehavior,
            padding: scrollTrackOutside ? '15px' : undefined,
            overflowX: overflowXHidden ? 'hidden' : 'scroll',
            overflowY: overflowYHidden ? 'hidden' : 'scroll',
            ...(overflowXHidden ? { marginBottom: 0 } : {}),
            ...(overflowYHidden ? { marginRight: 0 } : {}),
          }}
          {...rest}
        />
      )}
      renderThumbHorizontal={({ style, ...props }) => (
        <div
          {...props}
          className="thumb-horizontal"
          style={{
            ...style,
            right: scrollTrackOutside ? '3px' : undefined,
            backgroundColor,
            borderRadius: 10,
            display: overflowXHidden ? 'none' : 'block',
            zIndex: 100,
          }}
        />
      )}
      renderThumbVertical={({ style, ...props }) => (
        <div
          {...props}
          className="thumb-vertical"
          style={{
            ...style,
            bottom: scrollTrackOutside ? '3px' : undefined,
            backgroundColor,
            borderRadius: 10,
            display: overflowYHidden ? 'none' : 'block',
            zIndex: 100,
          }}
        />
      )}
    >
      <Box
        sx={mergeSxProps(
          {
            overflowAnchor: 'none',
            paddingBottom: startAtBottom ? 0 : paddingBottom,
            ...(stableScrollHeight && stableContentMinHeight > 0
              ? { minHeight: stableContentMinHeight }
              : {}),
          },
          innerSx,
        )}
        ref={setInnerContentRef}
      >
        {children}
      </Box>
      {startAtBottom && (
        <div
          className="scroll-box-anchor"
          style={{ overflowAnchor: 'auto', height: paddingBottom }}
        />
      )}
    </Scrollbars>
  );
}
